import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { projects } from '../../mocks/projects';

interface MetaConfig {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogImageAlt?: string;
  canonical?: string;
  ogTitle?: string;
}

/**
 * Static meta configuration for regular pages.
 * The key must match the pathname exactly (e.g. "/portfolio").
 */
const pageMetaConfig: Record<string, MetaConfig> = {
  '/': {
    title: 'Strategic Solutions for Businesses Solving Complex Problems | Giedrius Petrauskas',
    description:
      'Discuss Your Project',
    keywords:
      'web developer, product creator, digital solutions, e-commerce development, web applications, Lithuania developer',
    ogImage:
      'https://static.readdy.ai/image/0b415033dc3c77175cdee172dc914215/3fa65f62f46d37444399a4931651de3a.png',
    ogImageAlt:
      'Giedrius Petrauskas - Strategic Consultant & Founder of Stelvar Holdings',
    canonical: `${import.meta.env.VITE_SITE_URL}/`,
    ogTitle: 'Turning complex ideas into practical digital systems and real results.',
  },
  '/portfolio': {
    title: 'Portfolio – Giedrius Petrauskas | Web Development Projects',
    description:
      'Explore my portfolio of custom websites, e-commerce solutions, and digital products. Real examples of modern web development projects built for clients across Europe and USA.',
    keywords:
      'portfolio, web development projects, e-commerce solutions, digital products, React applications, TypeScript projects',
    ogImage:
      'https://static.readdy.ai/image/0b415033dc3c77175cdee172dc914215/aa301a2f2729a992abe5fd98c15ef5d8.jpeg',
    ogImageAlt: 'Giedrius Petrauskas Portfolio - Web Development Projects',
    canonical: `${import.meta.env.VITE_SITE_URL}/portfolio`,
  },
  '/contact': {
    title: 'Contact – Giedrius Petrauskas | Get in Touch',
    description:
      'Ready to start your project? Contact Giedrius Petrauskas for custom web development, e-commerce platforms, or digital product creation. Fast response, global service.',
    keywords:
      'contact developer, hire web developer, project inquiry, web development services, Lithuania developer contact',
    ogImage:
      'https://static.readdy.ai/image/0b415033dc3c77175cdee172dc914215/aa301a2f2729a992abe5fd98c15ef5d8.jpeg',
    ogImageAlt: 'Contact Giedrius Petrauskas - Web Developer',
    canonical: `${import.meta.env.VITE_SITE_URL}/contact`,
  },
  '/ai-twin': {
    title: 'AI Twin – Your Digital Self, Available 24/7 | Giedrius Petrauskas',
    description:
      'Create an AI Twin — a digital version of yourself trained on your voice, knowledge and content. Let your audience talk to you anytime. Built for mentors, coaches and creators in 2–4 weeks.',
    keywords:
      'AI Twin, AI mentor, digital clone, voice AI, knowledge platform, mentor AI, creator AI, AI coaching',
    ogImage:
      'https://static.readdy.ai/image/0b415033dc3c77175cdee172dc914215/1b1022cef1323f1cfee988ca6394d22f.png',
    ogImageAlt: 'AI Twin – Digital version of a mentor available 24/7',
    canonical: `${import.meta.env.VITE_SITE_URL}/ai-twin`,
    ogTitle: 'What if people could talk to you anytime — even when you\'re not there?',
  },
  '/partners': {
    title: 'AI Twin Partner Program – Earn $2,000 Per Referral | Giedrius Petrauskas',
    description:
      'Join the AI Twin Partner Program and earn $2,000 for every mentor or creator you introduce. No limit on referrals. We handle everything — you just make the introduction.',
    keywords:
      'AI Twin partner, referral program, earn commission, mentor referral, AI partner program, $2000 commission',
    ogImage:
      'https://public.readdy.ai/ai/img_res/f367f77105a131e1fee5a4463002e698.jpg',
    ogImageAlt: 'AI Twin Partner Program – Earn $2,000 per successful referral',
    canonical: `${import.meta.env.VITE_SITE_URL}/partners`,
    ogTitle: 'Earn $2,000 by introducing mentors to their AI Twin',
  },
  '/privacy-policy': {
    title: 'Privacy Policy – Giedrius Petrauskas',
    description:
      'Privacy policy for giedriuspetrauskas.com. Learn how we collect, use, and protect your personal information when using our web development services.',
    keywords: 'privacy policy, data protection, personal information',
    canonical: `${import.meta.env.VITE_SITE_URL}/privacy-policy`,
  },
  '/cookie-policy': {
    title: 'Cookie Policy – Giedrius Petrauskas',
    description:
      'Cookie policy for giedriuspetrauskas.com. Learn about the cookies we use and how to manage your preferences.',
    keywords: 'cookie policy, cookies, website tracking',
    canonical: `${import.meta.env.VITE_SITE_URL}/cookie-policy`,
  },
  '/terms-of-use': {
    title: 'Terms of Use – Giedrius Petrauskas',
    description:
      'Terms of use for giedriuspetrauskas.com. Read the terms and conditions for using our website and services.',
    keywords: 'terms of use, terms and conditions, website terms',
    canonical: `${import.meta.env.VITE_SITE_URL}/terms-of-use`,
  },
};

/**
 * Generates a meta configuration for a project based on its slug.
 * Returns `null` when the project cannot be found.
 */
function getProjectMeta(slug: string): MetaConfig | null {
  const project = projects.find((p) => p.slug === slug);
  if (!project) return null;

  // fullDescription can be an object or a plain string
  let rawDescription = '';
  if (typeof project.fullDescription === 'object' && project.fullDescription !== null) {
    const fd = project.fullDescription as { problem?: string; solution?: string; outcome?: string };
    rawDescription = [fd.problem, fd.solution, fd.outcome].filter(Boolean).join(' ');
  } else {
    rawDescription = (project.fullDescription as string) || (project as any).description || '';
  }
  const description = rawDescription.slice(0, 155);

  return {
    title: `${project.name} – ${project.category} | Giedrius Petrauskas`,
    description,
    keywords: `${project.tags?.join(', ') ?? ''}, ${project.category}, web development project`,
    ogImage: project.image,
    ogImageAlt: `${project.name} - ${project.category} project by Giedrius Petrauskas`,
    canonical: `${import.meta.env.VITE_SITE_URL}/portfolio/${project.slug}`,
  };
}

/**
 * Detects the MIME type of an image based on its URL extension.
 */
function getImageMimeType(url: string): string {
  if (!url) return 'image/jpeg';
  const lower = url.toLowerCase().split('?')[0];
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  return 'image/jpeg';
}

/**
 * Injects or updates a Schema.org JSON-LD <script> tag for the current page.
 */
function updatePageSchema(path: string, meta: MetaConfig): void {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://giedriuspetrauskas.com';
  const scriptId = 'schema-dynamic-page';

  let schema: Record<string, unknown> | null = null;

  if (path === '/') {
    schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: meta.title,
      description: meta.description,
      url: `${siteUrl}/`,
      inLanguage: 'en-US',
      isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'Giedrius Petrauskas' },
    };
  } else if (path === '/portfolio') {
    schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: meta.title,
      description: meta.description,
      url: `${siteUrl}/portfolio`,
      inLanguage: 'en-US',
      isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'Giedrius Petrauskas' },
    };
  } else if (path === '/contact') {
    schema = {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: meta.title,
      description: meta.description,
      url: `${siteUrl}/contact`,
      inLanguage: 'en-US',
      isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'Giedrius Petrauskas' },
      mainEntity: {
        '@type': 'Person',
        name: 'Giedrius Petrauskas',
        email: 'giedrius@giedriuspetrauskas.com',
        url: siteUrl,
        jobTitle: 'Developer & Product Creator',
      },
    };
  } else if (path.startsWith('/portfolio/')) {
    const slug = path.replace('/portfolio/', '');
    const project = projects.find((p) => p.slug === slug);
    if (project) {
      schema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: meta.title,
        description: meta.description,
        url: `${siteUrl}/portfolio/${slug}`,
        inLanguage: 'en-US',
        isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'Giedrius Petrauskas' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Portfolio', item: `${siteUrl}/portfolio` },
            { '@type': 'ListItem', position: 3, name: project.name, item: `${siteUrl}/portfolio/${slug}` },
          ],
        },
      };
    }
  }

  if (!schema) {
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    return;
  }

  let scriptEl = document.getElementById(scriptId);
  if (!scriptEl) {
    scriptEl = document.createElement('script');
    scriptEl.id = scriptId;
    scriptEl.setAttribute('type', 'application/ld+json');
    document.head.appendChild(scriptEl);
  }
  scriptEl.textContent = JSON.stringify(schema);
}

/**
 * React component responsible for synchronising the document meta‑tags
 * with the current route. It deliberately returns `null` because it does
 * not render any visual output.
 */
export default function DynamicMeta(): null {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;

    // Determine which meta configuration we need
    let meta: MetaConfig | null = null;

    // Project‑specific pages live under `/portfolio/<slug>`
    if (path.startsWith('/portfolio/') && path !== '/portfolio/') {
      const slug = path.replace('/portfolio/', '');
      meta = getProjectMeta(slug);
    }

    // Fallback to static page config or home page
    if (!meta) {
      meta = pageMetaConfig[path] ?? pageMetaConfig['/'];
    }

    // ----- Update the document head -----
    try {
      // Title
      document.title = meta.title;

      // Standard meta tags
      updateMetaTag('name', 'description', meta.description);
      if (meta.keywords) {
        updateMetaTag('name', 'keywords', meta.keywords);
      }

      // Canonical link
      if (meta.canonical) {
        updateLinkTag('canonical', meta.canonical);
      }

      // Open Graph
      updateMetaTag('property', 'og:title', meta.ogTitle || meta.title);
      updateMetaTag('property', 'og:description', meta.description);
      updateMetaTag('property', 'og:type', 'website');
      updateMetaTag('property', 'og:site_name', 'Giedrius Petrauskas');
      updateMetaTag('property', 'og:locale', 'en_US');
      if (meta.ogImage) {
        updateMetaTag('property', 'og:image', meta.ogImage);
        updateMetaTag('property', 'og:image:width', '1200');
        updateMetaTag('property', 'og:image:height', '630');
        updateMetaTag('property', 'og:image:type', getImageMimeType(meta.ogImage));
      }
      if (meta.ogImageAlt) {
        updateMetaTag('property', 'og:image:alt', meta.ogImageAlt);
      }
      if (meta.canonical) {
        updateMetaTag('property', 'og:url', meta.canonical);
      }

      // Twitter Card
      updateMetaTag('name', 'twitter:card', 'summary_large_image');
      updateMetaTag('name', 'twitter:title', meta.ogTitle || meta.title);
      updateMetaTag('name', 'twitter:description', meta.description);
      if (meta.ogImage) {
        updateMetaTag('name', 'twitter:image', meta.ogImage);
      }
      if (meta.ogImageAlt) {
        updateMetaTag('name', 'twitter:image:alt', meta.ogImageAlt);
      }
      if (meta.canonical) {
        updateMetaTag('name', 'twitter:url', meta.canonical);
      }

      // Last‑modified (simple YYYY‑MM‑DD format)
      const today = new Date().toISOString().split('T')[0];
      updateMetaTag('name', 'last-modified', today);

      // Inject page-level Schema.org JSON-LD
      updatePageSchema(path, meta);
    } catch (err) {
      // In production we do not want the whole app to crash because
      // meta‑tag manipulation failed.
      console.error('DynamicMeta: failed to update document head', err);
    }
  }, [location]);

  // This component does not render anything.
  return null;
}

/**
 * Helper that creates or updates a `<meta>` element.
 *
 * @param attribute - Either "name" or "property" depending on the spec.
 * @param key       - The value of the attribute (e.g. "description").
 * @param content   - The content to set.
 */
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string): void {
  if (!content) return; // Guard against empty strings

  let element = document.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

/**
 * Helper that creates or updates a `<link rel="canonical">` element.
 */
function updateLinkTag(rel: string, href: string): void {
  if (!href) return; // Guard against empty hrefs

  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.href = href;
}
